import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { MembershipAllowedTimes } from 'src/features/membership/types';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { Badge } from 'src/registry/ui/Badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { cn } from 'src/utils/shadcn';

/*
 * This is a cell used to display all days of a week and colour only the enabled ones
 */
const formatTime = (date: string) => date.split('.')[0];

function CellAllowedDays<TData, TValue extends MembershipAllowedTimes[]>({ cell }: CellContext<TData, TValue>) {
    const allowedTimes: MembershipAllowedTimes[] = cell.getValue();
    const { formatMessage } = useIntl();

    if (!allowedTimes.length) return '-';

    return (
        <>
            {Object.values(DAY_OF_WEEK).map((day: DAY_OF_WEEK) => {
                const allowedTime = allowedTimes.find((time) => time.day_of_week === day);
                const weekDay = formatMessage(membershipMessages[day]);
                return (
                    <TooltipProvider key={`${cell.id}-${day}`}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Badge
                                    variant="tag"
                                    className={cn(
                                        'mr-1 flex size-6 shrink-0 items-center justify-center bg-white px-0 uppercase opacity-30 last:mr-0',
                                        allowedTime?.day_of_week === day && 'font-semibold opacity-100',
                                    )}
                                >
                                    {weekDay.charAt(0)}
                                </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="flex flex-col gap-2">
                                <span className="text-gray-500">{weekDay}</span>
                                {allowedTime
                                    ? `${formatTime(allowedTime.from_time)} - ${formatTime(allowedTime.to_time)}`
                                    : formatMessage(membershipMessages.notAllowed)}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            })}
        </>
    );
}

export { CellAllowedDays };
